import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Stack, vars } from '@etg/wings';
import { string as stringUtil } from '@eti/utils';
import { DangerousHtmlContent, Notification, variants } from '@eti/components';
import { css, etiColors } from '@eti/styles';
import { Trip, TripCampaign } from '@eti/types';
import { useProperty, useSearch, useTranslation } from '@eti/providers';
import { search as searchPropTypes } from '../../../../widgets/search-form/propTypes';
import PriceDropProtectionNotification from '../../../../common/components/PriceDropProtectionNotification';
import TrustFactsPromo from '../../../../common/components/TrustFactsPromo';
import TripNotAvailableErrorModal from '../../../../common/error-modals/TripNotAvailableErrorModal';
import { isDesktopViewport } from '../../../../utils/view';
import { getFlightResultToInsertAdFormAfter } from '../../utils/resultUtils';
import { useFreeCancellationDaysAheadNotification } from '../../../../common/custom-hooks/freeCancellationDaysAheadNotification';
import { addAdFormScriptTags } from '../adForm';
import ValidWithVoucherSwitch from '../ValidWithVoucherSwitch';
import LoadingSpinner from './LoadingSpinner';
import FlightResult from './FlightResult';
import QuickSortButtonBar from './QuickSortButtonBar';

const wrapperStyles = css`
  background-color: ${vars.colors.page.background.primary};
  height: 100%;
`;

export const Wrapper = (props) => <div className={wrapperStyles} {...props} />;

const noMoreFlightsWrapperStyles = css`
  margin-top: 28px;
  text-align: center;
`;

const noMoreFlightsTitleStyles = css`
  font-size: 1rem;
  font-weight: 700;
`;

const noMoreFlightsLabelStyles = css`
  color: ${etiColors.grayScale[10]};
  font-size: 0.75rem;
`;

const SearchResults = ({
  addTrip,
  flightResults,
  isMultiStop,
  isSelectTripLoading,
  isSelfServiceRebooking,
  onCloseTripNotAvailableModal,
  onShowAncillaryBundleModal,
  onShowValidWithVoucher,
  onSort,
  quickSortPrices,
  redirectToDBF,
  redirectToTravelerDetails,
  search,
  setFetchMoreResults,
  shouldShowLoadingSpinner,
  shouldShowTripIsNotAvailableModal,
  shouldShowValidWithVoucher,
  tripCampaigns,
}) => {
  const flightResultsWrapper = useRef(null);
  const { t } = useTranslation();
  const { p } = useProperty();

  const { currentSearchData } = useSearch();
  const { selectedSortType } = currentSearchData;
  const hasAllFlightsBeenFetched = search.filteredFlightsCount === flightResults.length;
  const freeCancellationDaysAheadNotification = useFreeCancellationDaysAheadNotification(
    search?.origin?.departuredAt,
  );

  useEffect(() => {
    const showAdForm = p('IbeClient.Adform.Enable');
    const adFormMid = isDesktopViewport()
      ? p('IbeClient.Adform.Result.Desktop.Mid')
      : p('IbeClient.Adform.Result.Mobile.Mid');
    const shouldShowAdForm = showAdForm && adFormMid;
    const { current } = flightResultsWrapper;

    if (shouldShowAdForm && current) {
      const flightResultToInsertAdFormAfter = getFlightResultToInsertAdFormAfter(current);

      if (flightResultToInsertAdFormAfter) {
        addAdFormScriptTags(flightResultToInsertAdFormAfter, adFormMid, search);
      }
    }
  }, [p, search]);

  const lastElementOfTrips = document.querySelector(
    `.resultPage-flightResults-${[flightResults.length - 1]}`,
  );

  useEffect(() => {
    const prefetchTravelerDetails = async () => {
      const TravelerDetailsPage = await import('../../../traveler-details');
      TravelerDetailsPage.default.preload();
    };
    if (lastElementOfTrips?.children) {
      prefetchTravelerDetails();
    }
  }, [lastElementOfTrips]);

  const getTripCampaignForFlight = (flightResult) =>
    tripCampaigns.find((tripCampaign) => tripCampaign.selectionKey === flightResult.selectionKey);

  const notificationText = freeCancellationDaysAheadNotification
    ? t('BookingFlow.FreeCancellation.DaysAhead.Text')
    : t('BookingFlow.FreeCancellation.Text');

  const notificationTestId = freeCancellationDaysAheadNotification
    ? 'freeCancellationInformationDaysAhead-search-result'
    : 'freeCancellationInformation-search-result';

  return (
    <>
      {p('PriceDropProtection.DisplayBanners.Enabled') && (
        <PriceDropProtectionNotification
          linkText={t('Result.PriceDropProtection.LinkText')}
          title={t('Result.PriceDropProtection.Title')}
        />
      )}
      {p('Result.Page.QuickFilters.Enabled') && (
        <QuickSortButtonBar
          onSort={onSort}
          selectedSortType={selectedSortType}
          sortPrices={quickSortPrices}
        />
      )}
      {isSelfServiceRebooking && p('Result.SelfServiceRebooking.ValidWithVoucherSwitch.Enable') && (
        <ValidWithVoucherSwitch
          onShowValidWithVoucher={onShowValidWithVoucher}
          shouldShowValidWithVoucher={shouldShowValidWithVoucher}
        />
      )}
      {(freeCancellationDaysAheadNotification || p('BookingFlow.FreeCancellation.Enable')) && (
        <Notification dataTestId={notificationTestId} variant={variants.CONFIRMATION}>
          <DangerousHtmlContent content={notificationText} />
        </Notification>
      )}
      <Stack data-testid="resultPage-searchResults" ref={flightResultsWrapper} spacing={24}>
        {flightResults.map((flightResult, index) => (
          <FlightResult
            addTrip={addTrip}
            index={index}
            isMultiStop={isMultiStop}
            isSelectTripLoading={isSelectTripLoading}
            key={flightResult.id}
            onShowAncillaryBundleModal={onShowAncillaryBundleModal}
            redirectToDBF={redirectToDBF}
            redirectToTravelerDetails={redirectToTravelerDetails}
            trip={flightResult}
            tripCampaign={getTripCampaignForFlight(flightResult)}
          />
        ))}
      </Stack>
      {hasAllFlightsBeenFetched && (
        <div className={noMoreFlightsWrapperStyles}>
          <p className={noMoreFlightsTitleStyles}>{t('Result.ScrollNoMoreResults.Title')}</p>
          <p className={noMoreFlightsLabelStyles}>
            {stringUtil.insertArgument(
              t('Result.ScrollNoMoreResults.Label'),
              flightResults.length,
              flightResults.length,
            )}
          </p>
        </div>
      )}
      {shouldShowLoadingSpinner && !hasAllFlightsBeenFetched && (
        <LoadingSpinner setFetchMoreResults={setFetchMoreResults} />
      )}
      <TrustFactsPromo />
      <TripNotAvailableErrorModal
        isOpen={shouldShowTripIsNotAvailableModal}
        onPrimaryAction={onCloseTripNotAvailableModal}
      />
    </>
  );
};

SearchResults.propTypes = {
  addTrip: PropTypes.func,
  flightResults: PropTypes.arrayOf(Trip),
  isMultiStop: PropTypes.bool,
  isSelectTripLoading: PropTypes.bool,
  isSelfServiceRebooking: PropTypes.bool,
  onCloseTripNotAvailableModal: PropTypes.func,
  onShowAncillaryBundleModal: PropTypes.func,
  onShowValidWithVoucher: PropTypes.func,
  onSort: PropTypes.func,
  quickSortPrices: PropTypes.objectOf(PropTypes.number),
  redirectToDBF: PropTypes.func,
  redirectToTravelerDetails: PropTypes.func,
  search: searchPropTypes,
  setFetchMoreResults: PropTypes.func,
  shouldShowLoadingSpinner: PropTypes.bool,
  shouldShowTripIsNotAvailableModal: PropTypes.bool,
  shouldShowValidWithVoucher: PropTypes.bool,
  tripCampaigns: PropTypes.arrayOf(TripCampaign),
};

export default SearchResults;
